// Extra reducer handlers for wiki thunks
// This file builds the extraReducers for the wiki slice

import {
  fetchSpaces, createSpace, updateSpace, deleteSpace,
  updateSpaceCover, uploadSpaceCoverImage,
  archiveSpace, restoreSpace, fetchArchivedSpaces
} from './wikiSpacesThunks'

import {
  fetchPages, fetchPageContent, createPage, updatePage,
  deletePage, movePage, togglePageFavorite,
  archivePage, restorePage, fetchArchivedPages
} from './wikiPagesThunks'

import { searchPages } from './wikiSearchThunks'
import { recordPageView, saveScrollPosition } from './wikiReadingThunks'
import {
  handleRealtimeWikiSpaceEvent,
  handleRealtimeWikiPageEvent
} from '../reducers/realtimeReducers'

/**
 * Build all extra reducers for wiki thunks
 * @param {import('@reduxjs/toolkit').ActionReducerMapBuilder} builder
 */
export const buildWikiExtraReducers = (builder) => {
  builder
    // === SPACES ===

    // Fetch spaces
    .addCase(fetchSpaces.pending, (state) => {
      state.loading.spaces = true
      state.error.spaces = null
    })
    .addCase(fetchSpaces.fulfilled, (state, action) => {
      state.loading.spaces = false
      state.spaces = action.payload
      state.initialized.spaces = true
    })
    .addCase(fetchSpaces.rejected, (state, action) => {
      state.loading.spaces = false
      state.error.spaces = action.payload
    })

    // Create space
    .addCase(createSpace.pending, (state) => {
      state.loading.createSpace = true
    })
    .addCase(createSpace.fulfilled, (state, action) => {
      state.loading.createSpace = false
      state.spaces.push(action.payload)
    })
    .addCase(createSpace.rejected, (state, action) => {
      state.loading.createSpace = false
      state.error.spaces = action.payload
    })

    // Update space
    .addCase(updateSpace.pending, (state) => {
      state.loading.updateSpace = true
    })
    .addCase(updateSpace.fulfilled, (state, action) => {
      state.loading.updateSpace = false
      const index = state.spaces.findIndex(s => s.id === action.payload.id)
      if (index !== -1) {
        state.spaces[index] = action.payload
      }
    })
    .addCase(updateSpace.rejected, (state, action) => {
      state.loading.updateSpace = false
      state.error.spaces = action.payload
    })

    // Delete space
    .addCase(deleteSpace.pending, (state) => {
      state.loading.deleteSpace = true
    })
    .addCase(deleteSpace.fulfilled, (state, action) => {
      state.loading.deleteSpace = false
      const spaceId = action.payload
      state.spaces = state.spaces.filter(s => s.id !== spaceId)
      state.pages = state.pages.filter(p => p.space_id !== spaceId)
      // Also clear from archived lists (permanent delete from the Archived view)
      state.archivedSpaces = state.archivedSpaces.filter(s => s.id !== spaceId)
      state.archivedPages = state.archivedPages.filter(p => p.space_id !== spaceId)
      if (state.currentSpaceId === spaceId) {
        state.currentSpaceId = null
        state.currentPageId = null
      }
    })
    .addCase(deleteSpace.rejected, (state, action) => {
      state.loading.deleteSpace = false
      state.error.spaces = action.payload
    })

    // Archive space (cascade-archives its pages)
    .addCase(archiveSpace.pending, (state) => {
      state.loading.archive = true
      state.error.archive = null
    })
    .addCase(archiveSpace.fulfilled, (state, action) => {
      state.loading.archive = false
      const { spaceId, space } = action.payload
      const archived = space || state.spaces.find(s => s.id === spaceId)
      // Remove space + its pages from active state
      state.spaces = state.spaces.filter(s => s.id !== spaceId)
      state.pages = state.pages.filter(p => p.space_id !== spaceId)
      // Surface in the Archived view immediately if it's already loaded
      if (state.initialized.archived && archived &&
          !state.archivedSpaces.find(s => s.id === spaceId)) {
        state.archivedSpaces.unshift(archived)
      }
      if (state.currentSpaceId === spaceId) {
        state.currentSpaceId = null
        state.currentPageId = null
      }
    })
    .addCase(archiveSpace.rejected, (state, action) => {
      state.loading.archive = false
      state.error.archive = action.payload
    })

    // Restore space (and its archived pages)
    .addCase(restoreSpace.pending, (state) => {
      state.loading.archive = true
      state.error.archive = null
    })
    .addCase(restoreSpace.fulfilled, (state, action) => {
      state.loading.archive = false
      const { space, pages } = action.payload
      if (space && !state.spaces.find(s => s.id === space.id)) {
        state.spaces.push(space)
        state.spaces.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      }
      for (const page of pages) {
        if (!state.pages.find(p => p.id === page.id)) {
          state.pages.push(page)
        }
      }
      // Remove restored items from the archived lists
      if (space) {
        state.archivedSpaces = state.archivedSpaces.filter(s => s.id !== space.id)
        state.archivedPages = state.archivedPages.filter(p => p.space_id !== space.id)
      }
    })
    .addCase(restoreSpace.rejected, (state, action) => {
      state.loading.archive = false
      state.error.archive = action.payload
    })

    // Fetch archived spaces (Archived view)
    .addCase(fetchArchivedSpaces.pending, (state) => {
      state.loading.archived = true
      state.error.archived = null
    })
    .addCase(fetchArchivedSpaces.fulfilled, (state, action) => {
      state.loading.archived = false
      state.archivedSpaces = action.payload
      state.initialized.archived = true
    })
    .addCase(fetchArchivedSpaces.rejected, (state, action) => {
      state.loading.archived = false
      state.error.archived = action.payload
    })

    // Update space cover
    .addCase(updateSpaceCover.fulfilled, (state, action) => {
      const updatedSpace = action.payload
      const index = state.spaces.findIndex(s => s.id === updatedSpace.id)
      if (index !== -1) {
        state.spaces[index] = updatedSpace
      }
    })

    // Upload space cover image
    .addCase(uploadSpaceCoverImage.fulfilled, (state, action) => {
      const updatedSpace = action.payload
      const index = state.spaces.findIndex(s => s.id === updatedSpace.id)
      if (index !== -1) {
        state.spaces[index] = updatedSpace
      }
    })

    // === PAGES ===

    // Fetch pages
    .addCase(fetchPages.pending, (state) => {
      state.loading.pages = true
      state.error.pages = null
    })
    .addCase(fetchPages.fulfilled, (state, action) => {
      state.loading.pages = false
      state.pages = action.payload
      state.initialized.pages = true
    })
    .addCase(fetchPages.rejected, (state, action) => {
      state.loading.pages = false
      state.error.pages = action.payload
    })

    // Fetch page content
    .addCase(fetchPageContent.pending, (state) => {
      state.loading.pageContent = true
      state.error.pageContent = null
    })
    .addCase(fetchPageContent.fulfilled, (state, action) => {
      state.loading.pageContent = false
      const page = action.payload
      state.pageContents[page.id] = {
        content: page.content,
        loadedAt: Date.now()
      }
      // Also update page metadata if it changed
      const pageIndex = state.pages.findIndex(p => p.id === page.id)
      if (pageIndex !== -1) {
        state.pages[pageIndex] = {
          ...state.pages[pageIndex],
          title: page.title,
          excerpt: page.excerpt,
          is_pinned: page.is_pinned,
          is_favorite: page.is_favorite,
          updated_at: page.updated_at
        }
      }
    })
    .addCase(fetchPageContent.rejected, (state, action) => {
      state.loading.pageContent = false
      state.error.pageContent = action.payload
    })

    // Create page
    .addCase(createPage.pending, (state) => {
      state.loading.createPage = true
    })
    .addCase(createPage.fulfilled, (state, action) => {
      state.loading.createPage = false
      state.pages.push(action.payload)
      // Also add to content cache
      state.pageContents[action.payload.id] = {
        content: action.payload.content,
        loadedAt: Date.now()
      }
    })
    .addCase(createPage.rejected, (state, action) => {
      state.loading.createPage = false
      state.error.pages = action.payload
    })

    // Update page
    .addCase(updatePage.pending, (state) => {
      state.loading.updatePage = true
    })
    .addCase(updatePage.fulfilled, (state, action) => {
      state.loading.updatePage = false
      const updatedPage = action.payload
      const index = state.pages.findIndex(p => p.id === updatedPage.id)
      if (index !== -1) {
        state.pages[index] = {
          ...state.pages[index],
          ...updatedPage
        }
      }
      // Update content cache
      if (updatedPage.content !== undefined) {
        state.pageContents[updatedPage.id] = {
          content: updatedPage.content,
          loadedAt: Date.now()
        }
      }
    })
    .addCase(updatePage.rejected, (state, action) => {
      state.loading.updatePage = false
      state.error.pages = action.payload
    })

    // Delete page
    .addCase(deletePage.pending, (state) => {
      state.loading.deletePage = true
    })
    .addCase(deletePage.fulfilled, (state, action) => {
      state.loading.deletePage = false
      const pageId = action.payload
      // Remove page and all its children (they have this page in their path)
      state.pages = state.pages.filter(p =>
        p.id !== pageId && !p.path?.includes(pageId)
      )
      // Also clear from archived list (permanent delete from the Archived view)
      state.archivedPages = state.archivedPages.filter(p =>
        p.id !== pageId && !p.path?.includes(pageId)
      )
      delete state.pageContents[pageId]
      if (state.currentPageId === pageId) {
        state.currentPageId = null
      }
    })
    .addCase(deletePage.rejected, (state, action) => {
      state.loading.deletePage = false
      state.error.pages = action.payload
    })

    // Archive page (cascade-archives its sub-tree)
    .addCase(archivePage.pending, (state) => {
      state.loading.archive = true
      state.error.archive = null
    })
    .addCase(archivePage.fulfilled, (state, action) => {
      state.loading.archive = false
      const pageIds = action.payload.pageIds || []
      const idSet = new Set(pageIds)
      state.pages = state.pages.filter(p => !idSet.has(p.id))
      for (const id of pageIds) {
        delete state.pageContents[id]
      }
      if (idSet.has(state.currentPageId)) {
        state.currentPageId = null
      }
    })
    .addCase(archivePage.rejected, (state, action) => {
      state.loading.archive = false
      state.error.archive = action.payload
    })

    // Restore page (and its archived sub-tree)
    .addCase(restorePage.pending, (state) => {
      state.loading.archive = true
      state.error.archive = null
    })
    .addCase(restorePage.fulfilled, (state, action) => {
      state.loading.archive = false
      const pages = action.payload.pages || []
      const restoredIds = new Set(pages.map(p => p.id))
      for (const page of pages) {
        if (!state.pages.find(p => p.id === page.id)) {
          state.pages.push(page)
        }
      }
      state.archivedPages = state.archivedPages.filter(p => !restoredIds.has(p.id))
    })
    .addCase(restorePage.rejected, (state, action) => {
      state.loading.archive = false
      state.error.archive = action.payload
    })

    // Fetch archived pages (Archived view)
    .addCase(fetchArchivedPages.pending, (state) => {
      state.loading.archived = true
      state.error.archived = null
    })
    .addCase(fetchArchivedPages.fulfilled, (state, action) => {
      state.loading.archived = false
      state.archivedPages = action.payload
      state.initialized.archived = true
    })
    .addCase(fetchArchivedPages.rejected, (state, action) => {
      state.loading.archived = false
      state.error.archived = action.payload
    })

    // Move page
    .addCase(movePage.pending, (state) => {
      state.loading.movePage = true
    })
    .addCase(movePage.fulfilled, (state, action) => {
      state.loading.movePage = false
      const updatedPage = action.payload
      const index = state.pages.findIndex(p => p.id === updatedPage.id)
      if (index !== -1) {
        state.pages[index] = {
          ...state.pages[index],
          ...updatedPage
        }
      }
    })
    .addCase(movePage.rejected, (state, action) => {
      state.loading.movePage = false
      state.error.pages = action.payload
    })

    // Toggle favorite
    .addCase(togglePageFavorite.pending, (state) => {
      state.loading.toggleFavorite = true
    })
    .addCase(togglePageFavorite.fulfilled, (state, action) => {
      state.loading.toggleFavorite = false
      const updatedPage = action.payload
      const index = state.pages.findIndex(p => p.id === updatedPage.id)
      if (index !== -1) {
        state.pages[index] = {
          ...state.pages[index],
          is_favorite: updatedPage.is_favorite
        }
      }
    })
    .addCase(togglePageFavorite.rejected, (state, action) => {
      state.loading.toggleFavorite = false
      state.error.pages = action.payload
    })

    // === SEARCH ===

    .addCase(searchPages.pending, (state) => {
      state.loading.search = true
      state.error.search = null
    })
    .addCase(searchPages.fulfilled, (state, action) => {
      state.loading.search = false
      state.searchResults = action.payload
    })
    .addCase(searchPages.rejected, (state, action) => {
      state.loading.search = false
      state.error.search = action.payload
    })

    // === CONTINUE READING ===

    .addCase(recordPageView.fulfilled, (state, action) => {
      const { pageId, last_viewed_at } = action.payload
      const pageIndex = state.pages.findIndex(p => p.id === pageId)
      if (pageIndex !== -1) {
        state.pages[pageIndex] = {
          ...state.pages[pageIndex],
          last_viewed_at
        }
      }
    })

    .addCase(saveScrollPosition.fulfilled, (state, action) => {
      const { pageId, scroll_position } = action.payload
      const pageIndex = state.pages.findIndex(p => p.id === pageId)
      if (pageIndex !== -1) {
        state.pages[pageIndex] = {
          ...state.pages[pageIndex],
          scroll_position
        }
      }
    })

    // === REALTIME EVENTS ===

    // Handle realtime wiki space events (cross-tab sync)
    .addCase(handleRealtimeWikiSpaceEvent.fulfilled, (state, action) => {
      if (!action.payload) return

      const { type, record, oldRecord } = action.payload

      switch (type) {
        case 'INSERT':
          // Add new space if not already present (avoid duplicates from optimistic updates)
          if (!state.spaces.find(space => space.id === record.id)) {
            state.spaces.push(record)
            // Sort by display_order after insertion
            state.spaces.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          }
          break

        case 'UPDATE': {
          const spaceIndex = state.spaces.findIndex(space => space.id === record.id)
          if (record.is_archived) {
            // Archived (possibly in another tab) → drop from active state + its pages
            if (spaceIndex !== -1) state.spaces.splice(spaceIndex, 1)
            state.pages = state.pages.filter(page => page.space_id !== record.id)
            if (state.currentSpaceId === record.id) {
              state.currentSpaceId = null
              state.currentPageId = null
            }
            // Surface in the Archived view if it's loaded
            if (state.initialized.archived && !state.archivedSpaces.find(s => s.id === record.id)) {
              state.archivedSpaces.unshift(record)
            }
          } else {
            if (spaceIndex !== -1) {
              state.spaces[spaceIndex] = { ...state.spaces[spaceIndex], ...record }
            } else {
              // Restored (possibly in another tab) → add back into active state
              state.spaces.push(record)
              state.spaces.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            }
            // No longer archived → drop from the Archived view
            state.archivedSpaces = state.archivedSpaces.filter(space => space.id !== record.id)
          }
          break
        }

        case 'DELETE': {
          const spaceToDelete = oldRecord || record
          if (spaceToDelete?.id) {
            state.spaces = state.spaces.filter(space => space.id !== spaceToDelete.id)
            // Also remove pages belonging to deleted space
            state.pages = state.pages.filter(page => page.space_id !== spaceToDelete.id)
            // Keep archived lists in sync
            state.archivedSpaces = state.archivedSpaces.filter(space => space.id !== spaceToDelete.id)
            state.archivedPages = state.archivedPages.filter(page => page.space_id !== spaceToDelete.id)
            // Clear current selection if it was this space
            if (state.currentSpaceId === spaceToDelete.id) {
              state.currentSpaceId = null
              state.currentPageId = null
            }
          }
          break
        }
      }
    })

    // Handle realtime wiki page events (cross-tab sync)
    .addCase(handleRealtimeWikiPageEvent.fulfilled, (state, action) => {
      if (!action.payload) return

      const { type, record, oldRecord } = action.payload

      switch (type) {
        case 'INSERT':
          // Add new page if not already present (avoid duplicates from optimistic updates)
          if (!state.pages.find(page => page.id === record.id)) {
            state.pages.push(record)
          }
          break

        case 'UPDATE': {
          const pageIndex = state.pages.findIndex(page => page.id === record.id)
          if (record.is_archived) {
            // Archived (possibly in another tab) → drop page + its sub-tree from active state
            state.pages = state.pages.filter(page =>
              page.id !== record.id && !page.path?.includes(record.id)
            )
            delete state.pageContents[record.id]
            if (state.currentPageId === record.id) {
              state.currentPageId = null
            }
            if (state.initialized.archived && !state.archivedPages.find(p => p.id === record.id)) {
              state.archivedPages.unshift(record)
            }
          } else {
            if (pageIndex !== -1) {
              state.pages[pageIndex] = { ...state.pages[pageIndex], ...record }
            } else {
              // Restored (possibly in another tab) → add back into active state
              state.pages.push(record)
            }
            // Update content cache if content changed
            if (record.content !== undefined && state.pageContents[record.id]) {
              state.pageContents[record.id] = {
                ...state.pageContents[record.id],
                content: record.content,
                loadedAt: Date.now()
              }
            }
            // No longer archived → drop from the Archived view
            state.archivedPages = state.archivedPages.filter(page => page.id !== record.id)
          }
          break
        }

        case 'DELETE': {
          const pageToDelete = oldRecord || record
          if (pageToDelete?.id) {
            // Remove page and all its children (they have this page in their path)
            state.pages = state.pages.filter(page =>
              page.id !== pageToDelete.id && !page.path?.includes(pageToDelete.id)
            )
            state.archivedPages = state.archivedPages.filter(page =>
              page.id !== pageToDelete.id && !page.path?.includes(pageToDelete.id)
            )
            // Clean up content cache
            delete state.pageContents[pageToDelete.id]
            // Clear current selection if it was this page
            if (state.currentPageId === pageToDelete.id) {
              state.currentPageId = null
            }
          }
          break
        }
      }
    })
}
